import React, { useState, useEffect } from "react";
import { Modal, Button, Input, ConfigProvider, message } from "antd";
import PropTypes from "prop-types";

/**
 * Shared "how should this secret reach the user?" chooser — one modal, two
 * delivery buttons (email vs copy). Reused by Reset Password (copy/email a
 * temp password) and Send New Invitation (email/copy an invite link); the two
 * differ only in title, description, and button labels, never in the flow.
 *
 * Two entry shapes, like the reset modal it generalises:
 *  - `targetEmail` provided → the target is known (a row action); shown as
 *    static text, no input.
 *  - `targetEmail` absent → the admin types the target email here.
 *
 * @param {boolean}  open
 * @param {boolean}  isSubmitting     - spinner + disable while the action runs.
 * @param {Function} onConfirm        - (email, deliveryKey) => void.
 * @param {Function} onCancel
 * @param {?string}  targetEmail
 * @param {string}   title
 * @param {React.ReactNode} description
 * @param {string}   [emailLabel]
 * @param {string}   [emailPlaceholder]
 * @param {Array<{key,label,icon,primary?}>} options - delivery buttons, in
 *   display order; Enter in the email input triggers the first option.
 * @param {number}   [width]
 */
export const DeliveryChoiceModal = ({
  open,
  isSubmitting = false,
  onConfirm,
  onCancel,
  targetEmail = null,
  title,
  description,
  emailLabel = "User email",
  emailPlaceholder = "Enter the user's email address",
  options,
  // Wide enough for the footer's longest known label set (Reset Password's
  // "Cancel" + "Copy temp password" + "Email temp password") to stay on one
  // row — at 520 the last button wrapped onto its own line below "Copy...".
  width = 580,
}) => {
  const [email, setEmail] = useState("");
  // Track which delivery button was clicked so only that one spins; the other
  // is disabled. (Sharing isSubmitting across both made both look fired.)
  const [clicked, setClicked] = useState(null);

  useEffect(() => {
    if (!open) {
      setEmail("");
      setClicked(null);
    }
  }, [open]);

  const effectiveEmail = targetEmail ?? email;
  const emailValid = effectiveEmail.trim().includes("@");

  const handleConfirm = (delivery) => {
    if (!emailValid) {
      message.error("Please enter a valid email address");
      return;
    }
    setClicked(delivery);
    onConfirm(effectiveEmail.trim(), delivery);
  };

  const enterKey = options?.[0]?.key;

  return (
    <Modal
      title={
        <div className="h-[3rem] flex items-center text-[#27272a] text-[1.5rem] font-[700] font-[Inter]">
          {title}
        </div>
      }
      open={open}
      onCancel={onCancel}
      centered
      width={width}
      styles={{ content: { borderRadius: "1.5rem" } }}
      footer={[
        <ConfigProvider
          theme={{ token: { colorLink: "#3f3f46" } }}
          key="cancel-provider"
        >
          <Button
            type="link"
            onClick={onCancel}
            disabled={isSubmitting}
            style={{
              fontFamily: "Inter",
              fontSize: "14px",
              fontWeight: 500,
              lineHeight: "24px",
              color: "#3f3f46",
              paddingInline: "8px",
            }}
          >
            Cancel
          </Button>
        </ConfigProvider>,
        ...options.map((opt) => {
          const btn = (
            <Button
              key={opt.key}
              type={opt.primary ? "primary" : "default"}
              icon={opt.icon}
              loading={isSubmitting && clicked === opt.key}
              disabled={!emailValid || (isSubmitting && clicked !== opt.key)}
              onClick={() => handleConfirm(opt.key)}
              style={{
                padding: opt.primary ? "0.5rem 1.5rem" : undefined,
                borderRadius: "1.25rem",
                height: "2.5rem",
                fontFamily: "Inter",
                fontWeight: 500,
              }}
            >
              {opt.label}
            </Button>
          );
          // The primary button keeps its own ConfigProvider wrapper, matching
          // the original reset modal's structure.
          return opt.primary ? (
            <ConfigProvider key={`${opt.key}-provider`}>{btn}</ConfigProvider>
          ) : (
            btn
          );
        }),
      ]}
    >
      <div className="flex flex-col gap-6 items-start pt-[1.5rem] font-[Inter]">
        {targetEmail ? (
          <div className="w-full flex flex-col gap-2">
            <label className="text-sm font-medium text-[#27272a]">
              {emailLabel}
            </label>
            <div className="text-[1rem] font-[600] text-[#27272a] break-all">
              {targetEmail}
            </div>
          </div>
        ) : (
          <div className="w-full flex flex-col gap-2">
            <label className="text-sm font-medium text-[#27272a]">
              {emailLabel} <span className="text-red-500">*</span>
            </label>
            <Input
              placeholder={emailPlaceholder}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onPressEnter={() => enterKey && handleConfirm(enterKey)}
              disabled={isSubmitting}
              size="large"
              autoFocus
            />
          </div>
        )}
        <div className="text-black text-[1rem] font-[400]">{description}</div>
        <div className="w-full h-[1px] bg-[#EAEAED] mb-1" />
      </div>
    </Modal>
  );
};

DeliveryChoiceModal.propTypes = {
  open: PropTypes.bool.isRequired,
  isSubmitting: PropTypes.bool,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  targetEmail: PropTypes.string,
  title: PropTypes.string.isRequired,
  description: PropTypes.node,
  emailLabel: PropTypes.string,
  emailPlaceholder: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      icon: PropTypes.node,
      primary: PropTypes.bool,
    }),
  ).isRequired,
  width: PropTypes.number,
};
